import _, { constant } from 'lodash';
import Enums from './enums';
import dateHelper from './dateHelper';
import constants from './constants';
import { jwtDecode } from 'jwt-decode';

// Sets an item in local storage with the specified key and string value.
const setItemToStorage = (key: string, value: string): void => {
  localStorage.setItem(key, value);
};

// Gets an item from local storage by key.
// It returns the string value if the key exists, otherwise null.
const getItemFromStorage = (key: string): string | null => {
  const item = localStorage.getItem(key);
  if (item) {
    return item;
  }
  return null;
};

// Removes an item from local storage by key.
const removeItem = (key: string): void => {
  localStorage.removeItem(key);
};

function getImagePath(imagePath: any) {
  if (!imagePath) {
    return 'https://stprod0001ts.blob.core.windows.net/cjsales/no-image.jpg';
  } else if (imagePath.indexOf('http') >= 0) {
    return imagePath;
  } else {
    return `${process.env.asset_url}${imagePath}`;
  }
}

function formatAddress(address: any): any {
  const line1 = `${address.addressLine1}, ${address.addressLine2 ? address.addressLine2 : ''
    }`;
  const line2 = `${address.city}, ${address.state} - ${address.postal}`;
  return { line1, line2 };
}

function getSelectedSite(selectedSiteId: any, sites: any): any {
  const selectedSites = sites.filter((s: any) => s.siteId === selectedSiteId);
  return _.first(selectedSites);
}

const getApiModeConfig = (): any => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  if (typeof apiUrl === 'undefined' || apiUrl === '') {
    console.error(
      'REACT_APP_API_BASE_URL is not defined. Using default configuration.'
    );
    return {
      apiMode: 'Unknown',
      liveMode: false,
    };
  }
  if (apiUrl.includes('uat')) {
    return {
      apiMode: 'Uat',
      liveMode: false,
    };
  } else if (apiUrl.includes(':8080')) {
    return {
      apiMode: 'Local',
      liveMode: false,
    };
  } else {
    return {
      apiMode: 'Live',
      liveMode: true,
    };
  }
};

function formatCurrency(money: any) {
  const formattedMoney = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(money);
  return formattedMoney;
}

function hasBreakfastItems(cartItem: any) {
  if (cartItem.type === Enums.comboType.quickCombo) {
    return _.some(
      cartItem.items,
      (comboItem) => comboItem.isBreakfast === true
    );
  } else if (cartItem.type === Enums.comboType.menuItem) {
    return cartItem.isBreakfast === true;
  }
  return false;
}

function formatSiteName(site: any): any {
  if (site && site.siteName) {
    return site.siteName.replace("Carl's Jr ", '');
  }
}

function getOrderModeMessage(
  orderMode: string,
  siteName: string,
  promiseDateTime: any
) {
  if (
    orderMode === Enums.orderMode.WalkIn ||
    orderMode === Enums.orderMode.EatIn
  ) {
    return `We have received your order and will strive to have it ready by ${dateHelper.displayTime(
      promiseDateTime
    )} at ${siteName}.\n\nAs your pick-up time approaches, please wait near the pickup area and listen for your name to be called.`;
  } else if (orderMode === Enums.orderMode.DriveThru) {
    return `We have received your order and will strive to have it ready by ${dateHelper.displayTime(
      promiseDateTime
    )} at ${siteName}.\n\nAs your pick-up time approaches, please make your way to the drive lane and notify our team of your arrival at the speaker box.`;
  }
};

function formatNumber(number: number): string {
  let formattedNumber: string = '0.00';
  if (typeof number === 'number') {
    formattedNumber = new Intl.NumberFormat('en-AU').format(number);
  }
  return formattedNumber;
};

async function getFromSecureStoreAsync(key: any) {
  const value = localStorage.getItem(key);
  if (value) {
    return JSON.parse(value);
  }
  return value;
};

async function validateToken() {
  const token = await getFromSecureStoreAsync(constants.localStorageKey.tokens);
  if (token) {
    const decodedToken = jwtDecode(token);
    let currentDate = new Date();
    if (!decodedToken.exp) {
      return false;
    }
    if (decodedToken?.exp * 1000 < currentDate.getTime()) {
      return false;
    }
    return true;
  }
};

const clearLocalStorage = () => localStorage.clear();

const handleNumericKey = (event: any) => {
  if (
    event.ctrlKey ||
    event.metaKey ||
    event.key === 'Tab' ||
    event.key === 'Backspace' ||
    event.key === 'x' ||
    event.key === ' ' ||
    event.key === 'ArrowRight' ||
    event.key === 'ArrowLeft'
  ) {
    return;
  }

  const numericRegex = /^[0-9.,]*$/;
  if (!numericRegex.test(event.key)) {
    event.preventDefault();
  }
};

const getShortName = (fullName: string) => {
  if (!fullName) return '';
  const parts = _.split(_.trim(fullName), ' ');
  if (_.size(parts) === 1) return parts[0];
  const firstName = _.first(parts);
  const lastInitial = _.first(_.last(parts));
  return `${firstName} ${lastInitial}`;
};

export default {
  getItemFromStorage,
  setItemToStorage,
  removeItem,
  getImagePath,
  formatAddress,
  getSelectedSite,
  getApiModeConfig,
  formatCurrency,
  hasBreakfastItems,
  formatSiteName,
  getOrderModeMessage,
  formatNumber,
  validateToken,
  clearLocalStorage,
  handleNumericKey,
  getShortName
};
